import React, { useEffect, useState } from 'react';
import { useTranslate } from '../lib/i18n';

interface ProgressStage {
  message: string;
  duration: number;
}

interface SurveyProgressIndicatorProps {
  currentStage: number;
  stages: ProgressStage[];
  startTime: number;
}

const SurveyProgressIndicator = ({
  currentStage,
  stages,
  startTime,
}: SurveyProgressIndicatorProps) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const t = useTranslate();

  useEffect(() => {
    if (startTime === 0) return;

    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const elapsedSeconds = Math.floor(elapsed / 1000);
      setElapsedTime(elapsedSeconds);
    }, 1000);

    return () => clearInterval(timer);
  }, [startTime]);

  const current = stages[currentStage] ||
    stages[0] || { message: t('survey.progress.processing'), duration: 1000 };
  const isLongWait = elapsedTime > 60;

  return (
    <div className="space-y-4 p-4 bg-card border rounded-md">
      {/* Current operation message */}
      <div className="text-center text-foreground">
        <span className="font-medium">{current.message}</span>
      </div>

      {/* Progress circles indicator - 6 balls, 1 per stage */}
      <div className="flex items-center justify-center space-x-1.5 px-4">
        {stages.map((_, index) => {
          const isCompleted = index < currentStage;
          const isCurrent = index === currentStage;

          return (
            <div
              key={index}
              className={`
                h-2.5 w-2.5 rounded-full transition-all duration-300 ease-out
                ${isCompleted ? 'bg-primary scale-110' : 'bg-muted scale-100'}
                ${isCurrent ? 'animate-pulse bg-primary' : ''}
              `}
            />
          );
        })}
      </div>

      {/* Time indicator messages */}
      <div className="text-sm text-center">
        {!isLongWait && (
          <div className="text-muted-foreground">{t('survey.progress.lessThanMinute')}</div>
        )}
        {isLongWait && <div className="text-warning">{t('survey.progress.takingLonger')}</div>}
      </div>
    </div>
  );
};

export default SurveyProgressIndicator;
